import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Sparkles, Truck, Heart } from "lucide-react";

const ETSY_URL = "https://www.etsy.com/uk/shop/ItIsObvious";
const ASSETS = "https://customer-assets-eiarnc6j.emergentagent.net/job_ddc48b91-037a-4cec-a375-a1288549b26a/artifacts/";

const EtsyPage = () => {
  const products = [
    {
      title: "PSILOCYBE MUSHROOM PRINT",
      price: "£8 – £150",
      tag: "PRINT",
      quip: "Earthy, moody, and very much legal. Hangs nicely above a sofa.",
      image: `${ASSETS}456dd80bff894d71_Rustic%20Mushroom%20Art%20Interior%20Vignette.png`,
      url: ETSY_URL,
      tagColor: "bg-[var(--obvious-coral)] text-white",
    },
    {
      title: "FLY AGARIC MUSHROOM PRINT",
      price: "£8 – £140",
      tag: "PRINT",
      quip: "The classic red toadstool. Don't eat it. Frame it.",
      image: `${ASSETS}06a91a83bdd4625c_Framed%20Fly%20Agaric%20Mushroom%20Watercolour.png`,
      url: ETSY_URL,
      tagColor: "bg-[var(--obvious-yellow)] text-black",
    },
    {
      title: "BLUE OYSTER MUSHROOM PRINT",
      price: "£8 – £140",
      tag: "PRINT",
      quip: "Cool blue fungi for people whose walls deserve better.",
      image: `${ASSETS}18b120db351f49c7_Blue.png`,
      url: ETSY_URL,
      tagColor: "bg-[var(--obvious-teal)] text-black",
    },
    {
      title: "CUSTOM LED VEGAS SIGN",
      price: "£950+",
      tag: "CUSTOM SIGN",
      quip: "Your name in lights, Vegas-style. Elvis not included.",
      image: `${ASSETS}904185fa45cdce74_Fabulous%20Turners%20Vegas%20Marquee%20Sign.png`,
      url: ETSY_URL,
      tagColor: "bg-[var(--obvious-purple)] text-white",
    },
    {
      title: "PROSECCO QUEEN GLITTER SIGN",
      price: "£160 – £240",
      tag: "GLITTER SIGN",
      quip: "For the friend who brings the bubbles. And the drama.",
      image: `${ASSETS}876fcf9f4db18a13_Sparkling%20Prosecco%20Queen%20Sign.png`,
      url: ETSY_URL,
      tagColor: "bg-[var(--obvious-coral)] text-white",
    },
  ];

  const perks = [
    {
      icon: Heart,
      title: "MADE BY HUMANS",
      desc: "Not a dropshipper in sight. Every piece from the collective is designed, drawn, cut, printed or sprayed by one of us.",
    },
    {
      icon: Truck,
      title: "SHIPPED UK-WIDE",
      desc: "Wrapped like a present, because that's basically what it is. Prints posted, signs couriered, art hand-packed.",
    },
    {
      icon: Sparkles,
      title: "NO TWO ALIKE",
      desc: "We don't do mass-produced. Even the reprints have personality. The one-offs? Pure, unrepeatable chaos.",
    },
  ];

  return (
    <main className="pt-20" data-testid="etsy-page">
      {/* Hero */}
      <section className="min-h-[70vh] relative bg-[var(--obvious-purple)] flex items-center overflow-hidden" data-testid="etsy-hero">
        {/* Floating shapes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-[8%] w-24 h-24 bg-[var(--obvious-coral)] rounded-full opacity-50 floating" />
          <div className="absolute top-40 right-[12%] w-20 h-20 bg-[var(--obvious-yellow)] rotate-45 opacity-50 floating" style={{ animationDelay: '1s' }} />
          <div className="absolute bottom-28 left-[18%] w-16 h-16 bg-[var(--obvious-teal)] opacity-60 floating" style={{ animationDelay: '2s' }} />
          <div className="absolute bottom-16 right-[8%] w-32 h-32 border-8 border-[var(--obvious-teal)] rounded-full opacity-40 spin-slow" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10 w-full">
          <div className="max-w-3xl">
            <span className="font-['Space_Mono'] text-lg bg-[var(--obvious-yellow)] text-black px-4 py-2 border-4 border-[var(--obvious-black)] inline-block mb-6">
              THE OBVIOUS SHOP
            </span>
            <h1 className="font-['Anton'] text-6xl md:text-8xl lg:text-9xl text-[var(--obvious-white)] leading-[0.85] mb-6">
              SHOP THE
              <br />
              <span className="text-[var(--obvious-teal)]">COLLECTIVE</span>
            </h1>
            <p className="font-['Space_Mono'] text-lg md:text-xl text-white/80 max-w-xl mb-8">
              Prints. One-offs. Custom signs. Custom art.
              <br />
              All made by the crew. All shipped straight from our Etsy.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href={ETSY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-obvious btn-coral inline-flex items-center gap-2"
                data-testid="etsy-hero-cta"
              >
                OPEN THE SHOP <ExternalLink size={24} strokeWidth={3} />
              </a>
              <Link
                to="/contact"
                className="btn-obvious btn-yellow inline-flex items-center gap-2"
                data-testid="etsy-hero-commission-cta"
              >
                COMMISSION A PIECE <ArrowRight size={24} strokeWidth={3} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="marquee-container bg-[var(--obvious-yellow)] py-4 border-y-4 border-[var(--obvious-black)]">
        <div className="marquee-content">
          <span className="font-['Anton'] text-2xl md:text-3xl text-[var(--obvious-black)] whitespace-nowrap">
            {"★ PRINTS ★ REPRINTS ★ ONE-OF-A-KINDS ★ CUSTOM SIGNS ★ CUSTOM ART ★ MADE BY THE COLLECTIVE ★ ".repeat(3)}
          </span>
        </div>
      </div>

      {/* Categories */}
      <section className="py-20 bg-[var(--obvious-white)] striped-bg relative" data-testid="etsy-categories">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="font-['Space_Mono'] text-lg bg-[var(--obvious-purple)] text-white px-4 py-2 border-4 border-[var(--obvious-black)] inline-block mb-4">
              FRESH FROM THE SHOP
            </span>
            <h2 className="font-['Anton'] text-5xl md:text-7xl text-[var(--obvious-black)] mb-4">
              CROWD PLEASERS
            </h2>
            <p className="font-['Space_Mono'] text-lg text-gray-700 max-w-2xl mx-auto">
              We're a <strong>collective of artists</strong> — so expect a loud mix of prints, art, and the occasional sign that refuses to stay quiet.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((p, index) => (
              <a
                key={p.title}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`group bg-[var(--obvious-white)] border-4 border-[var(--obvious-black)] shadow-[8px_8px_0px_0px_#000] hover:shadow-[12px_12px_0px_0px_#000] transition-[transform,box-shadow] duration-200 hover:-translate-y-2 ${index % 2 === 0 ? 'rotate-1' : '-rotate-1'} hover:rotate-0 flex flex-col`}
                data-testid={`etsy-product-${p.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <div className="relative aspect-square overflow-hidden border-b-4 border-[var(--obvious-black)]">
                  <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className={`absolute top-3 left-3 font-['Space_Mono'] text-xs font-bold px-3 py-1 border-4 border-[var(--obvious-black)] ${p.tagColor}`}>
                    {p.tag}
                  </span>
                  <span className="absolute bottom-3 right-3 font-['Anton'] text-xl bg-[var(--obvious-yellow)] text-black px-3 py-1 border-4 border-[var(--obvious-black)] -rotate-3" data-testid={`etsy-product-price-${index}`}>
                    {p.price}
                  </span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-['Anton'] text-2xl text-[var(--obvious-black)] mb-2 leading-tight">{p.title}</h3>
                  <p className="font-['Space_Mono'] text-sm text-gray-700 flex-1">{p.quip}</p>
                  <div className="mt-5 inline-flex items-center gap-2 font-['Anton'] text-sm text-[var(--obvious-coral)]">
                    BUY ON ETSY <ExternalLink size={16} strokeWidth={3} />
                  </div>
                </div>
              </a>
            ))}
            <a
              href={ETSY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-[var(--obvious-purple)] border-4 border-[var(--obvious-black)] shadow-[8px_8px_0px_0px_#000] hover:shadow-[12px_12px_0px_0px_#000] transition-[transform,box-shadow] duration-200 hover:-translate-y-2 rotate-1 hover:rotate-0 p-8 flex flex-col justify-between gap-6 min-h-[420px] relative overflow-hidden"
              data-testid="etsy-see-all-card"
            >
              <div className="absolute -top-10 -right-10 w-40 h-40 border-8 border-[var(--obvious-teal)] rounded-full opacity-50 spin-slow" />
              <div className="absolute bottom-24 -left-6 w-16 h-16 bg-[var(--obvious-yellow)] rotate-45 opacity-60" />
              <span className="font-['Space_Mono'] text-xs font-bold bg-[var(--obvious-yellow)] text-black px-3 py-1 border-4 border-[var(--obvious-black)] self-start relative z-10">
                PLOT TWIST
              </span>
              <div className="relative z-10">
                <h3 className="font-['Anton'] text-5xl text-white leading-[0.9] mb-4">
                  THERE'S
                  <br />
                  <span className="text-[var(--obvious-teal)]">LOADS</span>
                  <br />
                  MORE.
                </h3>
                <p className="font-['Space_Mono'] text-sm text-white/90">
                  This is just the shop window. The full collection lives on Etsy, and it's a bit of a rabbit hole.
                </p>
              </div>
              <div className="relative z-10 btn-obvious btn-yellow inline-flex items-center gap-2 self-start text-lg">
                SEE EVERYTHING <ArrowRight size={20} strokeWidth={3} />
              </div>
            </a>
          </div>
        </div>

        {/* Decorative */}
        <div className="absolute top-10 right-10 w-28 h-28 border-8 border-[var(--obvious-purple)] rounded-full opacity-20" />
        <div className="absolute bottom-20 left-10 w-20 h-20 bg-[var(--obvious-coral)] rotate-45 opacity-20" />
      </section>

      {/* Why Shop With Us */}
      <section className="py-20 bg-[var(--obvious-black)]" data-testid="etsy-perks">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Anton'] text-5xl md:text-7xl text-[var(--obvious-white)] mb-4">
              WHY BUY FROM US
            </h2>
            <p className="font-['Space_Mono'] text-lg text-gray-400 max-w-2xl mx-auto">
              Three reasons. Keep it obvious.
            </p>
            <div className="w-32 h-2 bg-[var(--obvious-teal)] mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {perks.map((perk, index) => (
              <div
                key={perk.title}
                className={`p-8 border-4 border-[var(--obvious-white)] bg-transparent hover:bg-[var(--obvious-purple)] transition-colors group ${index % 2 === 0 ? 'rotate-1' : '-rotate-1'} hover:rotate-0`}
                data-testid={`etsy-perk-${perk.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-16 h-16 bg-[var(--obvious-coral)] border-4 border-[var(--obvious-white)] flex items-center justify-center mb-6 group-hover:bg-[var(--obvious-yellow)] transition-colors">
                  <perk.icon size={32} strokeWidth={3} className="text-white group-hover:text-black" />
                </div>
                <h3 className="font-['Anton'] text-2xl text-[var(--obvious-white)] mb-4">
                  {perk.title}
                </h3>
                <p className="font-['Space_Mono'] text-sm text-gray-400 group-hover:text-gray-200 transition-colors">
                  {perk.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final Big CTA */}
      <section className="py-20 bg-[var(--obvious-coral)] relative overflow-hidden" data-testid="etsy-final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-['Anton'] text-5xl md:text-7xl text-[var(--obvious-white)] leading-[0.9] mb-6">
            GRAB
            <br />
            <span className="text-[var(--obvious-yellow)]">SOMETHING</span>
            <br />
            OBVIOUS
          </h2>
          <p className="font-['Space_Mono'] text-lg text-white/90 mb-10 max-w-2xl mx-auto">
            Can't commission? Can't wait? Dive straight into the Etsy shop. New drops, old favourites, and a few things we probably shouldn't have made.
          </p>
          <a
            href={ETSY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-obvious bg-[var(--obvious-black)] text-white inline-flex items-center gap-3 text-2xl"
            data-testid="etsy-final-cta-btn"
          >
            SHOP ON ETSY <ExternalLink size={28} strokeWidth={3} />
          </a>
        </div>

        <div className="absolute top-10 left-10 w-40 h-40 border-8 border-[var(--obvious-white)] rounded-full opacity-30 spin-slow" />
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-[var(--obvious-yellow)] opacity-30 rotate-45 floating" />
      </section>
    </main>
  );
};

export default EtsyPage;
